/* Importing the React library and the useCallback, useState, and useEffect hooks. */
import { useCallback, useState, useEffect } from 'react';

import PropTypes from 'prop-types';

// import Pusher from 'pusher-js';
import { io } from 'socket.io-client';

/* Importing the components from the material-ui library. */
import { Button, Grid } from '@mui/material';

/* Importing the sweetalert2 library. */
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import axios from '../api/http/Repository';

/* Importing from the components folder. */
import TelemetryCard from './TelemetryCard';

/* A way to access environment variables. */
const { REACT_APP_LIFELINE_BACKEND_URL, REACT_APP_PUSHER_EVENT } = process.env;

const MySwal = withReactContent(Swal);

Telemetry.propTypes = {
  Device: PropTypes.object,
  columnSize: PropTypes.object,
  standalone: PropTypes.bool,
};

/**
 * The MonitorDashboard component is used to register a device with the server.
 * It is used to register a device with the server.
 * @returns None
 */
export default function Telemetry(props) {
  const { Device, columnSize, standalone } = props;

  const [patientsList, setPatientsList] = useState([]);
  const [observationData, setObservationData] = useState({});
  const [requestID, setRequestID] = useState(null);
  const [socket, setSocket] = useState(null);

  const requestBP = async () => {
    const patientID = patientsList[0];
    // const { data } = await MonitorRepository.requestBP(patientID);
    MySwal.fire({
      title: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () =>
        // eslint-disable-next-line no-async-promise-executor, consistent-return, no-unused-vars
        new Promise(async (resolve, _reject) => {
          try {
            const formData = new FormData();
            formData.append('patientid', patientID);

            const { data } = await axios.post(`/api/requestBP`, formData);
            // console.log(data.RequestResult)
            setRequestID(data.RequestResult);
            // Swal.showValidationMessage('Waiting for device response');
            setTimeout(() => {
              resolve([data.RequestResult]);
            }, 15000);
          } catch (err) {
            return resolve(['error', err]);
          }
        }),
    }).then(async (result) => {
      if (result.dismiss) {
        return;
      }
      if (result.value[0] === 'error') {
        // console.log(result.value[1]);
        MySwal.fire({
          icon: 'error',
          title: 'Request BP failed.',
          text: result.value[1],
        });
      } else {
        Swal.close();
      }
    });
  };

  const confirmBPRequest = useCallback(() => {
    const LIMIT = 1;
    const SECONDS = 30;
    let cnt = 0;
    const query = setInterval(async () => {
      if (requestID) {
        if (cnt <= LIMIT) {
          const resp = await getBPRequest(requestID);
          // console.log(`resp ${  resp}`);
          if (resp > 0) {
            MySwal.fire({
              icon: 'success',
              title: 'BP Success.',
            });
            clearInterval(query);
          }
        } else {
          clearInterval(query);
        }
        cnt += 1;
      }
    }, 1000 * SECONDS);
  }, [requestID]);

  const getBPRequest = async (requestid) => {
    try {
      const formData = new FormData();
      formData.append('requestid', requestid);
      const { data } = await axios.post(`/api/getRequestBPValue`, formData);
      // console.log('bprequest data', data);
      return data.BPValue[0].rob_bp;
    } catch (e) {
      if (e) {
        // console.log(e);
        MySwal.fire({
          icon: 'error',
          title: 'Request BP failed.',
          text: e,
        });
      }
    }
    return null;
  };

  useEffect(() => {
    confirmBPRequest();
  }, [confirmBPRequest, requestID]);

  function getBaseUrl() {
    switch (process.env.REACT_APP_ENV) {
        case "PROD" : {
            return window.location.origin;
        }
        case "BETA" : {
            return "http://139.99.90.93";
        }
        case "DEV" : {
            return process.env.REACT_APP_LIFELINE_BACKEND_URL;
        }
        default: {
            return REACT_APP_LIFELINE_BACKEND_URL;
        }
    }
  }

  const initPusher = useCallback(() => {
    const event = REACT_APP_PUSHER_EVENT;
    const url = getBaseUrl();

    if (!patientsList || patientsList.length < 1) {
      return;
    }

    if (socket === null) {
      setSocket(
        io(url, {
          query: {
            patientID: patientsList,
          },
        })
      );
    } else {
      socket.on(event, (args) => {
        const data = args;
        console.log('PUSHER DATA', data);
        const observations = {
          ...observationData,
        };
        data.patientBasicObservation.forEach((obs) => {
          if (patientsList.includes(obs.tpo_subject.split('/')[1])) {
            observations[obs.tpo_subject] = { ...observations[obs.tpo_subject] };
            observations[obs.tpo_subject][obs.tpo_code] = {
              value: obs.tpo_value,
              effectivity: obs.tpo_effectivity,
            };
          }
        });
        // console.log(observations);
        setObservationData(observations);
      });

      socket.on('connect', () => {
        console.log('Connected to Socket.');
      });

      socket.connect();
    }
  }, [observationData, patientsList, socket]);

  /**
   * Initializes the data for the page.
   */
  const initializeData = useCallback(async () => {
    const patientsArray = [];
    if (Device.maxslot) {
      for (let i = 0; i < Device.maxslot; i += 1) {
        if (Device[`slot${i + 1}`] && Device[`slot${i + 1}`] !== '') {
          patientsArray.push(Device[`slot${i + 1}`]);
        }
      }
      setPatientsList(patientsArray);
    }
  }, [Device]);

  // [dispatch, navigate, params.id]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  useEffect(() => {
    initPusher();
  }, [initPusher]);

  const maxSlot = Device.maxslot || 6;
  const numSlots = Math.min(Math.max(maxSlot, 1), 10);

  const oddSlots = [];
  const evenSlots = [];

  for (let i = 0; i < numSlots; i += 1) {
    const slot = (
      <Grid item key={i} xs={12} md={standalone ? 12 : 6}>
        <TelemetryCard patientID={Device[`slot${i + 1}`]} observationData={observationData} columnSize={columnSize} />
      </Grid>
    );

    if (i % 2 === 0) {
      evenSlots.push(slot);
    } else {
      oddSlots.push(slot);
    }
  }

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        item
        xs={12}
        md={standalone ? 12 : 6}
        spacing={2}
      >
        {evenSlots}
      </Grid>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        item
        xs={12}
        md={standalone ? 12 : 6}
        spacing={2}
      >
        {oddSlots}
      </Grid>

      {/* {[...Array(Math.min(Math.max(Device.maxslot || 6, 1), 10))].map((x, i) => (
        <Grid item key={i} xs={12} md={standalone ? 12 : 6}>
          <TelemetryCard
            patientID={Device[`slot${i + 1}`]}
            observationData={observationData}
            columnSize={columnSize}
            notification={Device.notification || false}
            notificationSound={Device.notificationSound || 'default'}
          />
        </Grid>
      ))} */}
      {standalone && (
        <Grid container direction="row" justifyContent="center" alignItems="center" item xs={12} sx={{ pt: 3 }}>
          <Button onClick={requestBP} variant="outlined" size="large" sx={{ borderColor: 'grey.500', color: 'white' }}>
            BP NOW
          </Button>
        </Grid>
      )}
    </>
  );
}
